/*
  Аналитика сайта: GA4 + Microsoft Clarity одним модулем.

  Внешних пакетов нет — оба скрипта вставляются динамически и отложенно, тем
  же способом, что виджет Jivo в public/index.html: requestIdleCallback с
  таймаутом 5 с или первое действие пользователя (scroll / pointerdown /
  keydown / touchstart), что наступит раньше. До этого момента события
  копятся в очереди и уходят после загрузки — первый клик не теряется.

  Идентификаторы берутся из окружения: REACT_APP_GA_ID и REACT_APP_CLARITY_ID.
  Нет id у сервиса — сервис просто выключен, остальное работает.

  Вне production (NODE_ENV !== 'production') в сеть не уходит ничего: всё
  печатается в console.debug('[analytics]', …).

  Что НЕ отправляем: содержимое полей, телефоны, почты, тексты сообщений
  чата. В параметрах событий — только имя формы, endpoint и такие же
  безличные значения. В pageview срезаются query-параметры с персональными
  данными (см. STRIPPED_PARAMS).
*/

// Роуты без аналитики: вход, админка и портальная страница. Список общий с
// темой — там он же задаёт «всегда светлые» экраны, дублировать не нужно.
import { LIGHT_ONLY as NO_ANALYTICS_ROUTES } from "../theme/lightOnlyRoutes";

const GA_ID = process.env.REACT_APP_GA_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
const IS_PROD = process.env.NODE_ENV === "production";

// Параметры, которые нельзя слать в pageview: /notNetwork получает mac в
// адресе, это идентификатор устройства.
const STRIPPED_PARAMS = ["mac", "mac_address", "ls", "l", "phone", "email"];

const IDLE_TIMEOUT_MS = 5000;
const INTERACTION_EVENTS = ["scroll", "pointerdown", "keydown", "touchstart"];

let started = false; // init() уже звали
let loaded = false; // скрипты вставлены
const queue = [];

const debug = (...args) => {
  // eslint-disable-next-line no-console
  console.debug("[analytics]", ...args);
};

export const isExcluded = (pathname) =>
  NO_ANALYTICS_ROUTES.some((re) => re.test(pathname));

/* Путь для pageview: без хоста и без параметров из STRIPPED_PARAMS. */
export const cleanPath = (pathAndSearch) => {
  const [path, search = ""] = String(pathAndSearch).split("?");
  if (!search) return path;
  const params = new URLSearchParams(search);
  STRIPPED_PARAMS.forEach((p) => params.delete(p));
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
};

/* ───────── вставка скриптов ───────── */

const injectScript = (src) => {
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  document.head.appendChild(s);
  return s;
};

const loadGa = () => {
  if (!GA_ID) return;
  window.dataLayer = window.dataLayer || [];
  // именно function, а не стрелка: gtag читает arguments
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, {
    // page_view отправляем сами на смену роута, иначе SPA шлёт его дважды
    send_page_view: false,
    anonymize_ip: true,
  });
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
};

const loadClarity = () => {
  if (!CLARITY_ID) return;
  // заглушка-очередь до прихода их скрипта
  window.clarity =
    window.clarity ||
    function clarity() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  injectScript(`https://www.clarity.ms/tag/${CLARITY_ID}`);
};

const load = () => {
  if (loaded) return;
  loaded = true;
  loadGa();
  loadClarity();
  // всё, что накопилось до загрузки
  while (queue.length) {
    const item = queue.shift();
    item();
  }
};

const schedule = () => {
  const once = { once: true, passive: true };
  const onInteraction = () => load();
  // взаимодействие ждём с самого начала: оно всегда позже первой отрисовки
  INTERACTION_EVENTS.forEach((ev) =>
    window.addEventListener(ev, onInteraction, once)
  );
  // А вот idle ставим только после load. Если звать requestIdleCallback
  // сразу из init(), на быстрой машине поток свободен ещё до первой
  // отрисовки, и скрипт уходит раньше неё — замерено: запрос на 389 мс при
  // FCP 396 мс. Так же сделан отложенный Jivo в public/index.html.
  const idle = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(load, { timeout: IDLE_TIMEOUT_MS });
    } else {
      setTimeout(load, IDLE_TIMEOUT_MS);
    }
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
};

/* Отложить до загрузки скриптов либо выполнить сразу. */
const whenLoaded = (fn) => {
  if (loaded) fn();
  else queue.push(fn);
};

/* ───────── публичный интерфейс ───────── */

export const init = (pathname = window.location.pathname) => {
  if (started) return;
  if (isExcluded(pathname)) {
    if (!IS_PROD) debug("init пропущен: роут без аналитики", pathname);
    return;
  }
  started = true;
  if (!IS_PROD) {
    debug("init (dev: в сеть ничего не уходит)", {
      ga: GA_ID || "выключен",
      clarity: CLARITY_ID || "выключен",
    });
    loaded = true; // очередь в dev не копим, сразу печатаем
    return;
  }
  if (!GA_ID && !CLARITY_ID) return; // оба сервиса выключены
  schedule();
};

export const pageview = (pathAndSearch) => {
  const path = cleanPath(pathAndSearch);
  if (isExcluded(path)) return;
  if (!IS_PROD) {
    debug("pageview", path);
    return;
  }
  if (!started) return;
  whenLoaded(() => {
    if (GA_ID && window.gtag) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.origin + path,
        page_title: document.title,
      });
    }
    // Clarity считает переходы сам по history API — вручную не шлём
  });
};

/*
  track(event, params)
  Имена событий фиксированные (см. README), параметры плоские: только
  строки и числа.
*/
export const track = (event, params = {}) => {
  if (!IS_PROD) {
    debug("event", event, params);
    return;
  }
  if (!started) return;
  whenLoaded(() => {
    if (GA_ID && window.gtag) {
      window.gtag("event", event, params);
      // лид дублируем стандартным событием GA4 — под цели и аудитории
      if (event === "lead_submit") {
        window.gtag("event", "generate_lead", params);
      }
    }
    if (CLARITY_ID && window.clarity) {
      // в Clarity событие идёт без параметров: там это метка для фильтра
      window.clarity("event", event);
    }
  });
};

/*
  Теги сессии для Clarity: по ним фильтруются записи (тема, активная
  вкладка тарифов). В GA4 те же значения приходят параметрами событий.
*/
export const setTag = (key, value) => {
  if (!IS_PROD) {
    debug("set", key, value);
    return;
  }
  if (!started || !CLARITY_ID) return;
  whenLoaded(() => {
    if (window.clarity) window.clarity("set", key, String(value));
  });
};

/*
  Открытие чата Jivo. Колбэк обязан быть объявлен ДО инициализации виджета:
  он читает window.jivo_onOpen один раз при старте — проверено, если
  присвоить после загрузки, вызова не будет. Чужой колбэк, если он есть,
  сохраняем и зовём.
*/
export const attachJivoHooks = () => {
  if (typeof window === "undefined") return;
  if (window.__analyticsJivoHooked) return;
  window.__analyticsJivoHooked = true;
  const prev = window.jivo_onOpen;
  window.jivo_onOpen = function jivoOnOpen() {
    track("chat_open");
    if (typeof prev === "function") {
      try {
        prev.apply(this, arguments);
      } catch (e) {
        /* чужой колбэк не должен ронять наш */
      }
    }
  };
};

export default {
  init,
  pageview,
  track,
  setTag,
  isExcluded,
  cleanPath,
  attachJivoHooks,
};
